import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Import slide components
import {
  Slide01Title,
  Slide02Problem,
  Slide03Solution,
  SlideStudentView,
  SlideLessonView,
  SlideTeacherDashboard,
  Slide04RealTime,
  Slide05Teachers,
  Slide06Science,
  Slide07Mastery,
  Slide08ZPD,
  Slide09Gamification,
  Slide10Parents,
  SlideGivingBack,
  SlideGettingStarted,
  SlideThankYou,
} from '../components/slides';

const slides = [
  Slide01Title,        // 1. Title
  Slide02Problem,      // 2. The Problem
  Slide03Solution,     // 3. Introducing Lumen8
  SlideStudentView,    // 4. Student Learning View
  SlideLessonView,     // 5. Interactive Lessons / Question Artefacts
  SlideTeacherDashboard, // 6. Teacher Dashboard
  Slide04RealTime,     // 7. Real-Time Updates
  Slide05Teachers,     // 8. Built for Teachers
  Slide06Science,      // 9. Learning Science
  Slide07Mastery,      // 10. Mastery & Spaced Repetition
  Slide08ZPD,          // 11. Zone of Proximal Development
  Slide09Gamification, // 12. Gamification
  Slide10Parents,      // 13. Parents
  SlideGivingBack,     // 14. One for One
  SlideGettingStarted, // 15. Getting Started
  SlideThankYou,       // 16. Thank You
];

const Presentation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const CurrentSlideComponent = slides[currentSlide];

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #0a0e27 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      {/* Main slide area */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <CurrentSlideComponent />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation bar */}
      <div
        className="flex items-center justify-between px-8 py-4"
        style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Left: Logo */}
        <div className="flex items-center gap-2">
          <span className="text-xl font-black bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent">
            Lumen8
          </span>
        </div>

        {/* Center: Progress dots */}
        <div className="flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'w-8 bg-[#0ea5e9]'
                  : 'bg-[#334155] hover:bg-[#475569]'
              }`}
            />
          ))}
        </div>

        {/* Right: Navigation buttons */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#64748b]">
            {currentSlide + 1} / {slides.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="p-2 rounded-lg bg-[#1e293b] hover:bg-[#334155] transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="p-2 rounded-lg bg-[#1e293b] hover:bg-[#334155] transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Presentation;
